/**
 * Coquille de l'application : barre latérale, en-tête collant, contenu.
 *
 * L'en-tête porte la fraîcheur des données. Ce n'est pas décoratif : un
 * graphique qui s'arrête hier a l'air d'une panne tant qu'on n'a pas dit que
 * c'était normal.
 *
 * Pas de bouton « recharger » en web app installée : l'en-tête en porte un sur
 * téléphone, on peut aussi tirer la page vers le bas (cf. `PullToRefresh`), et
 * les données se relancent seules au retour au premier plan (cf.
 * `useResumeRefresh`).
 *
 * Les onglets d'usage de l'app (Vue d'ensemble, Apprentissage…) ne
 * s'affichent que dans Analytics : ailleurs, la barre latérale suffit.
 */

import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { useResumeRefresh } from '../hooks/useResumeRefresh';
import { formatInstant } from '../lib/format';
import { ChatWidget } from './ChatWidget';
import { InstallBanner } from './InstallBanner';
import { PullToRefresh } from './PullToRefresh';
import { SectionNav } from './SectionNav';
import { Sidebar } from './Sidebar';

interface LayoutProps {
  email: string | null;
  lastSyncAt?: string | null;
  onSignOut: () => void;
  children: ReactNode;
}

export function Layout({ email, lastSyncAt, onSignOut, children }: LayoutProps) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // L'assistant lit toute la base : réservé aux admins (l'API le refuse aussi).
  const { role } = useAuth();
  const inUsage = pathname === '/usage' || pathname.startsWith('/usage/');
  useResumeRefresh();

  return (
    <div className="app-shell">
      <Sidebar open={menuOpen} email={email} onNavigate={() => setMenuOpen(false)} onSignOut={onSignOut} />
      {/* Voile derrière la barre ouverte, sur mobile : un clic à côté la referme. */}
      {menuOpen && <div className="sidebar-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />}

      <div className="app-column">
        <header className="app-header">
          <button
            type="button"
            className="btn app-header__menu"
            aria-expanded={menuOpen}
            aria-label="Ouvrir le menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            ☰
          </button>
          <h1 className="app-header__brand">
            Piano LED Analytics
            {lastSyncAt !== undefined && (
              <span className="app-header__sub">données à jour au {formatInstant(lastSyncAt ?? null)}</span>
            )}
          </h1>

          <span className="app-header__spacer" />

          {email && (
            <Link className="app-header__sub app-header__account" to="/compte" title="Mot de passe, second facteur">
              {email}
            </Link>
          )}
          {/* Ordinateur : Déconnexion. Téléphone : un bouton recharger à la
              place (la web app installée n'a pas celui du navigateur), la
              déconnexion passe en bas du tiroir — bascule en CSS. */}
          <button type="button" className="btn app-header__signout" onClick={onSignOut}>
            Déconnexion
          </button>
          <button type="button" className="btn app-header__reload" onClick={() => window.location.reload()} aria-label="Recharger" title="Recharger">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" />
            </svg>
          </button>
        </header>

        <main className="app-main">
          {inUsage && <SectionNav />}
          {children}
        </main>

        <PullToRefresh />
        <InstallBanner />
        {role === 'admin' && <ChatWidget />}
      </div>
    </div>
  );
}
